import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  IconButton,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { useState } from "react";
import { DatePickerField } from "src/shared/components";
import { useDownloadReconciliationActMutation } from "../model/api/api";

const MIN_RECONCILIATION_DATE = dayjs("2025-07-31");

const DEFAULT_RECONCILIATION_ERROR = "Не удалось сформировать акт сверки";

const getRequestErrorMessage = (error: unknown) => {
  if (typeof error !== "object" || error === null || !("data" in error)) {
    return DEFAULT_RECONCILIATION_ERROR;
  }

  const data = (error as { data?: unknown }).data;

  if (
    typeof data === "object" &&
    data !== null &&
    "message" in data &&
    typeof data.message === "string"
  ) {
    return data.message;
  }

  return DEFAULT_RECONCILIATION_ERROR;
};

interface IReconciliationActModalProps {
  open: boolean;
  partnerId: string;
  partnerName: string;
  contractId: number | null;
  onClose: () => void;
}

export const ReconciliationActModal = ({
  open,
  partnerId,
  contractId,
  partnerName,
  onClose,
}: IReconciliationActModalProps) => {
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(
    dayjs().startOf("month"),
  );

  const [dateTo, setDateTo] = useState<Dayjs | null>(dayjs());
  const [error, setError] = useState<string | null>(null);

  const [downloadReconciliationAct, { isLoading }] =
    useDownloadReconciliationActMutation();

  const isInvalidPeriod =
    !dateFrom ||
    !dateTo ||
    dateFrom.isBefore(MIN_RECONCILIATION_DATE, "day") ||
    dateTo.isBefore(MIN_RECONCILIATION_DATE, "day") ||
    dateFrom.isAfter(dateTo, "day");

  const handleClose = () => {
    if (isLoading) {
      return;
    }

    resetForm();
    onClose();
  };

  const resetForm = () => {
    setDateFrom(dayjs().startOf("month"));
    setDateTo(dayjs());
    setError(null);
  };

  const downloadPdfFile = (fileUrl: string, fileName: string) => {
    const link = document.createElement("a");

    link.href = fileUrl;
    link.download = fileName;

    document.body.appendChild(link);
    link.click();
    link.remove();

    URL.revokeObjectURL(fileUrl);
  };

  const handleDownloadAct = async () => {
    if (
      isInvalidPeriod ||
      !dateFrom ||
      !dateTo ||
      contractId === null ||
      !partnerId
    ) {
      return;
    }

    setError(null);

    try {
      const { fileUrl, fileName } = await downloadReconciliationAct({
        partnerId,
        contractId,
        date_from: dateFrom.format("YYYY-MM-DD"),
        date_to: dateTo.format("YYYY-MM-DD"),
      }).unwrap();

      downloadPdfFile(fileUrl, fileName || `Акт сверки ${partnerName}.pdf`);
    } catch (requestError) {
      setError(getRequestErrorMessage(requestError));
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        Сформировать акт сверки
        <IconButton onClick={handleClose} disabled={isLoading}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ pt: 2 }}>
          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={2}
          >
            <DatePickerField
              label="Дата от"
              value={dateFrom}
              onChange={(value) => {
                setDateFrom(value);
                setError(null);
              }}
              minDate={MIN_RECONCILIATION_DATE}
              maxDate={dateTo || dayjs()}
              disableFuture
              disableKeyboardInput
              size="small"
            />

            <DatePickerField
              label="Дата до"
              value={dateTo}
              onChange={(value) => {
                setDateTo(value);
                setError(null);
              }}
              minDate={dateFrom || MIN_RECONCILIATION_DATE}
              maxDate={dayjs()}
              disableFuture
              disableKeyboardInput
              size="small"
            />
          </Stack>

          {dateFrom && dateTo && dateFrom.isAfter(dateTo, "day") && (
            <Alert severity="error">
              Дата начала периода не может быть позже даты окончания.
            </Alert>
          )}

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button variant="outlined" onClick={handleClose} disabled={isLoading}>
          Закрыть
        </Button>

        <Button
          variant="contained"
          onClick={handleDownloadAct}
          disabled={
            isInvalidPeriod || isLoading || contractId === null || !partnerId
          }
          startIcon={
            isLoading ? (
              <CircularProgress size={18} color="inherit" />
            ) : undefined
          }
        >
          {isLoading ? "Формирование..." : "Сформировать акт сверки"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
